import {createSlice} from '@reduxjs/toolkit';

const initialState = {
  loader: false,
  fields: [],
  // The category the fields were loaded for. Field ids are only unique inside a
  // category, so nothing may be matched by id without checking this first.
  category_id: null,
};

const GRAVITY_DATA_TYPES = [
  'api_gravity_measured',
  'api_gravity_reported',
  'measured_gravity_minus_reported',
];

// LOUT sits a fixed distance above the freeze point: 18 degrees in Fahrenheit,
// 10 in Celsius. The unit comes from the `temp_unit` key on the field.
const LOUT_OFFSET_F = 18;
const LOUT_OFFSET_C = 10;

const loutOffset = tempUnit =>
  String(tempUnit ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .startsWith('C')
    ? LOUT_OFFSET_C
    : LOUT_OFFSET_F;

// Each option of the `validate_brix_ri_ph` field (the fluid brand select) holds
// the band the reading has to stay in, written as "34.3 - 35.9".
const BRIX_RI_PH_DATA_TYPES = ['brix', 'ri', 'ph'];

// The `brix_ri` key of the selected deice tool option names the scale the tool
// reads on; the other reading is the one it cannot measure.
const BRIX_RI_DATA_TYPES = ['brix', 'ri'];
const SCALE_NOT_MEASURED = {brix: 'ri', ri: 'brix'};

const parseReadingBand = band => {
  const parts = String(band ?? '').split('-');
  if (parts.length !== 2) {
    return null;
  }
  const [min, max] = parts.map(part =>
    part.trim() === '' ? NaN : Number(part.trim()),
  );
  return Number.isNaN(min) || Number.isNaN(max) ? null : [min, max];
};

function clearGravityFieldsInState(state) {
  state.fields.forEach(item => {
    if (GRAVITY_DATA_TYPES.includes(item?.data_type)) {
      item.answers = [];
    }
  });
}

const reducer = createSlice({
  name: 'inspection',
  initialState,
  reducers: {
    setLoader(state, action) {
      state.loader = action.payload;
    },
    setFields(state, action) {
      // Ensure state.fields is always an array to avoid length access errors
      if (!Array.isArray(state.fields)) {
        state.fields = [];
      }
      if (action.payload) {
        if (action.payload?.type === 'full') {
          state.fields = Array.isArray(action.payload?.fields)
            ? action.payload.fields
            : [];
          state.category_id = action.payload?.category_id ?? null;
        } else {
          if (state.fields.length > 0) {
            if (action.payload.delete) {
              state.fields = state.fields?.filter(
                elem => elem.id !== action.payload?.id,
              );
            } else {
              let s = state.fields.filter(
                item => item?.id === action.payload?.id,
              )[0];
              let a = state.fields.filter(
                item => item.id !== action.payload.id,
              );
              if (s) {
                if (action.payload.answers !== undefined) {
                  if (action.payload.type === 'sump_sample') {
                    if (s.answers.length === 0) {
                      s.answers = action.payload.answers;
                    } else {
                      if (action.payload.answers[0].match(/\D/g)) {
                        if (s.answers[0].length === 1) {
                          if (
                            s.answers[0].match(/\D/g) &&
                            action.payload.answers[0].match(/\D/g)
                          ) {
                            s.answers = [action.payload.answers[0]];
                          } else {
                            s.answers = [
                              s.answers[0][0] + action.payload.answers[0],
                            ];
                          }
                        } else if (s.answers[0].length > 1) {
                          s.answers = [
                            s.answers[0][0] + action.payload.answers[0],
                          ];
                        } else {
                          s.answers = [action.payload.answers[0]];
                        }
                      } else {
                        if (s.answers[0].length === 1) {
                          if (
                            s.answers[0].match(/\d/g) &&
                            action.payload.answers[0].match(/\d/g)
                          ) {
                            s.answers = [action.payload.answers[0]];
                          } else {
                            s.answers = [
                              action.payload.answers[0] + s.answers[0],
                            ];
                          }
                        } else if (s.answers[0].length > 1) {
                          s.answers = [
                            action.payload.answers[0] + s.answers[0][1],
                          ];
                        } else {
                          s.answers = [action.payload.answers[0]];
                        }
                      }
                      // if ()
                      // s.answers = action.payload.answers
                    }
                  } else {
                    s.answers = action.payload.answers;
                  }
                }
                if (action.payload.type === 'dry_rating') {
                  s.required = action.payload.required;
                }
                if (action.payload.has_comment !== undefined) {
                  s.has_comment = action.payload.has_comment;
                }
                if (action.payload.comment_text !== undefined) {
                  s.comment_text = action.payload.comment_text;
                }
                if (action.payload.comment_files !== undefined) {
                  s.comment_files = action.payload.comment_files;
                }
                if (action.payload.photo !== undefined) {
                  s.photo = action.payload.photo;
                }
                if (action.payload.photos !== undefined) {
                  s.photos = action.payload.photos;
                }
              } else {
                state.fields = [...a, action.payload];
              }
            }
          } else {
            state.fields = [action.payload];
          }
        }
      } else {
        state.fields = [];
        state.category_id = null;
      }

      // When api_gravity_measured or api_gravity_reported changes (or on full load), update measured_gravity_minus_reported
      const isGravityUpdate =
        action.payload?.data_type === 'api_gravity_measured' ||
        action.payload?.data_type === 'api_gravity_reported';
      const isFullLoad = action.payload?.type === 'full';
      if (action.payload && (isGravityUpdate || isFullLoad)) {
        const measuredField = state.fields.find(
          item => item?.data_type === 'api_gravity_measured',
        );
        const reportedField = state.fields.find(
          item => item?.data_type === 'api_gravity_reported',
        );
        const measured = measuredField?.answers?.[0];
        const reported = reportedField?.answers?.[0];
        const measuredNum = parseFloat(measured);
        const reportedNum = parseFloat(reported);
        if (!Number.isNaN(measuredNum) && !Number.isNaN(reportedNum)) {
          const value = measuredNum - reportedNum;
          state.fields.forEach(item => {
            if (item?.data_type === 'measured_gravity_minus_reported') {
              const formatted = value.toFixed(4);
              item.answers = [formatted.replace(/\.?0+$/, '') || '0'];
            }
          });
        }
      }

      // When safe_fill_height or gauge_tank_and_record_volume changes (or on full load), update calculated_ullage
      const isUllageUpdate =
        action.payload?.data_type === 'safe_fill_height' ||
        action.payload?.data_type === 'gauge_tank_and_record_volume';
      const isFullLoadUllage = action.payload?.type === 'full';
      if (action.payload && (isUllageUpdate || isFullLoadUllage)) {
        const safeFillField = state.fields.find(
          item => item?.data_type === 'safe_fill_height',
        );
        const gaugeField = state.fields.find(
          item => item?.data_type === 'gauge_tank_and_record_volume',
        );
        const safeFill = safeFillField?.answers?.[0];
        const gauge = gaugeField?.answers?.[0];
        const safeFillNum = parseFloat(safeFill);
        const gaugeNum = parseFloat(gauge);
        if (!Number.isNaN(safeFillNum) && !Number.isNaN(gaugeNum)) {
          const value = safeFillNum - gaugeNum;
          state.fields.forEach(item => {
            if (item?.data_type === 'calculated_ullage') {
              item.answers = [String(value)];
            }
          });
        }
      }

      // When calculated_ullage or volume_scheduled_for_receipt changes (or on full load), update tank_capacity_sufficient
      const isCapacityUpdate =
        action.payload?.data_type === 'calculated_ullage' ||
        action.payload?.data_type === 'volume_scheduled_for_receipt';
      const isFullLoadCapacity = action.payload?.type === 'full';
      if (action.payload && (isCapacityUpdate || isFullLoadCapacity)) {
        const ullageField = state.fields.find(
          item => item?.data_type === 'calculated_ullage',
        );
        const volumeField = state.fields.find(
          item => item?.data_type === 'volume_scheduled_for_receipt',
        );
        const ullage = ullageField?.answers?.[0];
        const volume = volumeField?.answers?.[0];
        const ullageNum = parseFloat(ullage);
        const volumeNum = parseFloat(volume);
        if (!Number.isNaN(ullageNum) && !Number.isNaN(volumeNum)) {
          const value = ullageNum >= volumeNum ? 'Y' : 'N';
          state.fields.forEach(item => {
            if (item?.data_type === 'tank_capacity_sufficient') {
              item.answers = [value];
            }
          });
        }
      }

      // When the fluid brand changes (or on full load), each of the brix, ri
      // and ph fields takes the acceptable range of the selected brand, which
      // is what the out of service warning is checked against.
      const brandField = state.fields.find(
        item => item?.data_type === 'validate_brix_ri_ph',
      );
      const isBrandUpdate =
        action.payload?.data_type === 'validate_brix_ri_ph' ||
        (brandField?.id !== undefined && action.payload?.id === brandField?.id);
      const isFullLoadBrand = action.payload?.type === 'full';
      // Without the brand field there is nothing to take a range from, and a
      // range the backend sent with the field is left as it is.
      if (brandField && action.payload && (isBrandUpdate || isFullLoadBrand)) {
        const brand = brandField?.answers?.[0];
        const bands = brandField?.options?.find(
          option => String(option?.value) === String(brand),
        );
        state.fields.forEach(item => {
          if (!BRIX_RI_PH_DATA_TYPES.includes(item?.data_type)) {
            return;
          }
          const range = parseReadingBand(bands?.[item.data_type]);
          if (range) {
            item.acceptable = {
              type: 'range',
              value: `${range[0]}|${range[1]}`,
            };
          } else {
            // No brand picked yet, or one whose band is missing: there is
            // nothing to check the reading against.
            delete item.acceptable;
          }
        });
      }

      // When freeze_point changes (or on full load), update lout
      const freezePointField = state.fields.find(
        item => item?.data_type === 'freeze_point',
      );
      // Not every input carries `data_type` in its payload, so the id of the
      // freeze point field counts as an update too.
      const isFreezePointUpdate =
        action.payload?.data_type === 'freeze_point' ||
        (freezePointField?.id !== undefined &&
          action.payload?.id === freezePointField?.id);
      const isFullLoadFreezePoint = action.payload?.type === 'full';
      if (action.payload && (isFreezePointUpdate || isFullLoadFreezePoint)) {
        const freezePointNum = parseFloat(freezePointField?.answers?.[0]);
        if (!Number.isNaN(freezePointNum)) {
          state.fields.forEach(item => {
            if (item?.data_type === 'lout') {
              // The lout field carries its own unit; the freeze point field is
              // the fallback, since the two are always read in the same unit.
              const offset = loutOffset(
                item?.temp_unit ?? freezePointField?.temp_unit,
              );
              const value = Math.round((freezePointNum + offset) * 10) / 10;
              item.answers = [String(value)];
            }
          });
        }
      }

      // LOUT is the lowest outside air temperature the fluid still protects at,
      // so it is the floor for that reading: below it the truck comes out of
      // service, at it the fluid is still usable. Same shape the backend stores
      // on the saved result (InspectionResultService::applyLoutAcceptable), so
      // the warning shown here and the corrective action raised there agree.
      const loutField = state.fields.find(item => item?.data_type === 'lout');
      const outsideTemperatureField = state.fields.find(
        item => item?.data_type === 'outside_temperature',
      );
      if (loutField && outsideTemperatureField) {
        const lout = parseFloat(loutField?.answers?.[0]);
        if (Number.isNaN(lout)) {
          // No LOUT to compare against yet.
          delete outsideTemperatureField.acceptable;
        } else {
          const value = String(lout);
          if (outsideTemperatureField.acceptable?.value !== value) {
            outsideTemperatureField.acceptable = {
              type: 'min',
              value,
              decommission: 'true',
            };
          }
        }
      }
    },
    // A deice tool reads on one scale only: the reading it does not measure is
    // hidden and kept blank, which is what the backend expects to receive.
    setToolScale(state, action) {
      const notMeasured = SCALE_NOT_MEASURED[action.payload] ?? null;
      state.fields.forEach(item => {
        if (!BRIX_RI_DATA_TYPES.includes(item?.data_type)) {
          return;
        }
        const hidden = notMeasured !== null && item.data_type === notMeasured;
        item.scale_hidden = hidden;
        if (hidden && item.answers?.length) {
          item.answers = [];
        }
      });
    },
    clearFields(state, action) {
      state.fields = action.payload;
      state.category_id = null;
    },
    clearGravityFields(state) {
      clearGravityFieldsInState(state);
    },
    setFile(state, action) {
      state.fields = state.fields.map(item => {
        if (item.id === action.payload.id) {
          item.comment_files = [
            ...item.comment_files,
            {uuid: action.payload.file},
          ];
        }
        return item;
      });
    },
  },
});

export const {
  setLoader,
  setFields,
  setToolScale,
  clearFields,
  clearGravityFields,
  setFile,
} = reducer.actions;
export default reducer.reducer;
